import { useQuery, type ApolloError } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import AvatarDetails from "@/components/AvatarDetails";
import Button from "@/components/Button";
import { SkeletonElement } from "@/components/Skeleton";
import { toDateTimeFormatWithTimezone } from "@/lib/dateHelpers";
import { formatPhoneNumber } from "@/lib/phoneHelpers";
import { sendToRaygun } from "@/lib/raygunUtils";
import { FLEX_CALL_DETAILS_QUERY, type FlexCallDetails as FlexCallDetailsData } from "../queries";
import { directionLabel, formatDuration, formatPercent, normalizeCallerIdentity } from "../_utils";
import GrokTranscriptCompare from "./GrokTranscriptCompare";
import RecordingPlayer from "./RecordingPlayer";
import useRecordingAudio from "./useRecordingAudio";

type Props = {
  callSid: string;
  hasRecording: boolean;
  personName?: string | null;
  personPgaId?: string | null;
  callerPhone?: string | null;
  interactionPath: string | null;
};

function SectionTitle({ children }: { children: string }) {
  return <h3 className="text-title-small text-sys-brand-on-surface">{children}</h3>;
}

type InteractionSectionProps = {
  callSid: string;
  personName?: string | null;
  personPgaId?: string | null;
  callerPhone?: string | null;
  interactionPath?: string | null;
};

// Caller identity comes from the calls-table row (no details query needed),
// so loading and error states render it too instead of hiding the caller.
// The View interaction action lives here so it exists in every state.
function InteractionSection({
  callSid,
  personName,
  personPgaId,
  callerPhone,
  interactionPath
}: InteractionSectionProps) {
  const navigate = useNavigate();
  const { name, phone } = normalizeCallerIdentity({ personName, callerPhone });
  return (
    <section aria-label="Interaction" className="flex flex-col gap-2">
      <SectionTitle>Interaction</SectionTitle>
      {name || phone || interactionPath ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <AvatarDetails
            title={name ?? "Unknown person"}
            secondaryText={personPgaId ? `PGA ID ${personPgaId}` : undefined}
            text={phone ?? "Phone unavailable"}
          />
          {interactionPath && (
            <Button variant="text" onClick={() => navigate(interactionPath)} testId={`view-interaction-${callSid}`}>
              View interaction
            </Button>
          )}
        </div>
      ) : (
        <p className="text-body-medium text-sys-brand-on-surface-variant">No linked interaction for this call.</p>
      )}
    </section>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col">
      <dt className="text-body-small text-sys-brand-on-surface-variant">{label}</dt>
      <dd className="text-body-medium text-sys-brand-on-surface">{value}</dd>
    </div>
  );
}

function topicLabel(details: FlexCallDetailsData): string {
  if (!details.transcriptTopic) return "—";
  const topic = details.transcriptSubtopic
    ? `${details.transcriptTopic} · ${details.transcriptSubtopic}`
    : details.transcriptTopic;
  if (details.transcriptTopicConfidence == null) return topic;
  return `${topic} (${formatPercent(details.transcriptTopicConfidence)})`;
}

function agentLabel(details: FlexCallDetailsData): string {
  if (details.workerName) return details.workerName;
  return details.direction === "inbound" ? "Unanswered" : "—";
}

function sentimentLabel(value: string | null): string {
  if (value == null || value === "") return "—";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

export default function CallDetails({
  callSid,
  hasRecording,
  personName,
  personPgaId,
  callerPhone,
  interactionPath
}: Props) {
  const { data, loading, error, refetch } = useQuery(FLEX_CALL_DETAILS_QUERY, {
    variables: { callSid },
    onError: (queryError: ApolloError) => {
      sendToRaygun(queryError, { tags: ["call-topics", "details"] });
    }
  });
  const recording = useRecordingAudio(callSid, hasRecording && error == null);
  const interactionProps = { callSid, personName, personPgaId, callerPhone, interactionPath };

  if (loading) {
    return (
      <div className="flex flex-col gap-6 py-4" data-testid="call-details-loading">
        <InteractionSection {...interactionProps} />
        <SkeletonElement height="h-4" width="w-40" rounded="rounded-full" />
        <SkeletonElement height="h-4" width="w-full" rounded="rounded-full" />
        <SkeletonElement height="h-4" width="w-3/4" rounded="rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-6 py-4">
        <InteractionSection {...interactionProps} />
        <div className="flex flex-col items-start gap-2" data-testid="call-topic-call-error">
          <p className="text-body-medium text-sys-brand-on-surface-variant">Couldn&apos;t load these call details.</p>
          <Button variant="text" onClick={() => refetch()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const details = data?.twilioFlexCall;

  return (
    <div className="flex flex-col gap-6 py-4" data-testid="call-topic-call-details">
      <InteractionSection {...interactionProps} />
      <section aria-label="Call details" className="flex flex-col gap-2">
        <SectionTitle>Details</SectionTitle>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
          <DetailItem
            label="Started"
            value={details?.startedAt ? (toDateTimeFormatWithTimezone(details.startedAt) ?? "—") : "—"}
          />
          <DetailItem label="Duration" value={formatDuration(details?.durationSeconds)} />
          <DetailItem label="Direction" value={directionLabel(details?.direction ?? null)} />
          <DetailItem label="Agent" value={details ? agentLabel(details) : "—"} />
          <DetailItem
            label="Queue wait"
            value={details?.queueTimeSeconds == null ? "—" : formatDuration(details.queueTimeSeconds)}
          />
          <DetailItem label="Sentiment" value={sentimentLabel(details?.sentimentLabel ?? null)} />
          <DetailItem label="Topic" value={details ? topicLabel(details) : "—"} />
          <DetailItem label="Caller" value={details?.callerName ?? "—"} />
          <DetailItem
            label="Phone"
            value={details?.participantPhone ? formatPhoneNumber(details.participantPhone) : "—"}
          />
        </dl>
      </section>

      <section aria-label="Call summary" className="flex flex-col gap-2">
        <SectionTitle>Summary</SectionTitle>
        <p className="text-body-medium text-sys-brand-on-surface-variant">
          {details?.transcriptSummary || "No AI summary for this call yet."}
        </p>
      </section>

      <section aria-label="Call transcript" className="flex flex-col gap-2">
        <SectionTitle>Transcript</SectionTitle>
        {details?.transcriptText ? (
          <p
            tabIndex={0}
            className="text-body-medium text-sys-brand-on-surface-variant max-h-48 overflow-y-auto whitespace-pre-wrap"
          >
            {details.transcriptText}
          </p>
        ) : (
          <p className="text-body-medium text-sys-brand-on-surface-variant">Transcript unavailable for this call.</p>
        )}
      </section>

      <GrokTranscriptCompare callSid={callSid} hasRecording={hasRecording} />

      <section aria-label="Call recording" className="flex flex-col gap-2">
        <SectionTitle>Recording</SectionTitle>
        {!hasRecording ? (
          <p className="text-body-medium text-sys-brand-on-surface-variant">No recording for this call.</p>
        ) : recording.loading ? (
          <SkeletonElement height="h-10" width="w-full" rounded="rounded-full" />
        ) : recording.audioUrl ? (
          <RecordingPlayer audioUrl={recording.audioUrl} callSid={callSid} />
        ) : (
          <p className="text-body-medium text-sys-brand-on-surface-variant">
            {recording.error ?? "Recording unavailable."}
          </p>
        )}
      </section>
    </div>
  );
}
